import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Phone, MessageSquare, Mail, Voicemail, User, Trash2, Pencil, ArrowDownLeft, ArrowUpRight } from "lucide-react";
import { useApi, errMsg, fmtDateTime, fmtDate } from "@/lib/api";
import { PageHeader, Empty } from "@/components/portal/StatusBadge";
import { CommForm } from "@/components/portal/CommForm";

export const CHANNELS = { call: ["Call", Phone], text: ["Text", MessageSquare], email: ["Email", Mail], voicemail: ["Voicemail", Voicemail], in_person: ["In person", User], other: ["Other", MessageSquare] };
const STATUS = { open: "bg-[#F2B91C]/25 text-[#7A5A00]", follow_up: "bg-red-50 text-red-700", resolved: "bg-green-50 text-green-700" };

const Row = ({ c, onEdit, onDelete, onStatus }) => {
  const [label, Icon] = CHANNELS[c.channel] || CHANNELS.other;
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-[#E6E2DA] bg-white p-4 sm:flex-row sm:items-start" data-testid={`comm-row-${c.id}`}>
      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#0E1F3F] text-[#F2B91C]"><Icon size={17} /></div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-semibold">{c.contact_name || c.contact_phone || c.contact_email || "Unknown"}</p>
          <span className="inline-flex items-center gap-0.5 text-xs text-[#8C877D]">{c.direction === "incoming" ? <ArrowDownLeft size={12} /> : <ArrowUpRight size={12} />}{label} {c.direction}</span>
          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${STATUS[c.status]}`} data-testid={`comm-status-${c.id}`}>{c.status.replace("_", " ")}</span>
        </div>
        {c.subject && <p className="mt-0.5 text-sm font-medium">{c.subject}</p>}
        {c.notes && <p className="mt-1 whitespace-pre-wrap text-sm text-[#4A4742]">{c.notes}</p>}
        <p className="mt-2 text-xs text-[#8C877D]">
          {fmtDateTime(c.received_at)} · logged by {c.logged_by}
          {c.contact_phone && <> · <a href={`tel:${c.contact_phone}`} className="hover:text-[#A67C00]">{c.contact_phone}</a></>}
          {c.contact_email && <> · <a href={`mailto:${c.contact_email}`} className="hover:text-[#A67C00]">{c.contact_email}</a></>}
          {c.project_name && ` · ${c.project_name}`}
          {c.follow_up_date && c.status !== "resolved" && <span className="font-semibold text-red-700"> · Follow up {fmtDate(c.follow_up_date)}</span>}
        </p>
      </div>
      <div className="flex shrink-0 gap-1.5">
        {c.status !== "resolved" && <button onClick={() => onStatus(c, "resolved")} data-testid={`comm-resolve-${c.id}`} className="rounded-full border border-[#E6E2DA] px-3 py-1.5 text-xs font-semibold hover:border-green-600 hover:text-green-700">Resolve</button>}
        <button onClick={() => onEdit(c)} data-testid={`comm-edit-${c.id}`} className="rounded-full border border-[#E6E2DA] p-2 hover:border-[#0E1F3F]"><Pencil size={13} /></button>
        <button onClick={() => onDelete(c)} data-testid={`comm-delete-${c.id}`} className="rounded-full border border-[#E6E2DA] p-2 hover:border-red-600 hover:text-red-600"><Trash2 size={13} /></button>
      </div>
    </div>
  );
};

export default function Communications() {
  const api = useApi();
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [channel, setChannel] = useState("all");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  useEffect(() => {
    api("get", "/communications").then(setItems);
  }, [api]);

  const save = (c) => setItems(items.some((x) => x.id === c.id) ? items.map((x) => (x.id === c.id ? c : x)) : [c, ...items]);
  const setSt = async (c, s) => {
    try {
      save(await api("put", `/communications/${c.id}`, { ...c, status: s }));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const remove = async (c) => {
    if (!window.confirm("Delete this entry?")) return;
    await api("delete", `/communications/${c.id}`);
    setItems(items.filter((x) => x.id !== c.id));
  };
  const shown = (items || []).filter((c) => (channel === "all" || c.channel === channel) && (status === "all" || c.status === status)
    && (!q || `${c.contact_name} ${c.contact_phone} ${c.contact_email} ${c.subject} ${c.notes}`.toLowerCase().includes(q.toLowerCase())));
  const count = (k) => (items || []).filter((c) => c.channel === k).length;
  const pill = (active) => `rounded-full border px-3.5 py-1.5 text-xs font-semibold ${active ? "border-[#0E1F3F] bg-[#0E1F3F] text-white" : "border-[#E6E2DA] bg-white hover:border-[#0E1F3F]"}`;

  return (
    <div data-testid="comms-page">
      <PageHeader eyebrow="Contact log" title="Calls, texts & emails">
        <button onClick={() => setEditing({})} data-testid="new-comm-btn" className="inline-flex items-center gap-2 rounded-full bg-[#F2B91C] px-5 py-2.5 text-sm font-bold text-[#0E1F3F] hover:bg-[#D9A30F]"><Plus size={16} />Log contact</button>
      </PageHeader>
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {["call", "text", "email"].map((k) => (
          <div key={k} className="rounded-xl border border-[#E6E2DA] bg-white p-4"><p className="eyebrow">{CHANNELS[k][0]}s</p><p className="font-mono-num mt-1 text-2xl font-semibold" data-testid={`comm-count-${k}`}>{count(k)}</p></div>
        ))}
        <div className="rounded-xl border border-[#E6E2DA] bg-white p-4"><p className="eyebrow">Open / follow-up</p><p className="font-mono-num mt-1 text-2xl font-semibold text-red-700" data-testid="comm-count-followup">{(items || []).filter((c) => c.status !== "resolved").length}</p></div>
      </div>
      <div className="mb-5 flex flex-wrap items-center gap-2">
        {["all", "call", "text", "email", "voicemail", "in_person"].map((k) => <button key={k} onClick={() => setChannel(k)} className={pill(channel === k)} data-testid={`comm-filter-${k}`}>{k === "all" ? "All" : CHANNELS[k][0]}</button>)}
        <span className="mx-1 h-5 w-px bg-[#E6E2DA]" />
        {["all", "open", "follow_up", "resolved"].map((k) => <button key={k} onClick={() => setStatus(k)} className={`${pill(status === k)} capitalize`} data-testid={`comm-status-filter-${k}`}>{k === "all" ? "Any status" : k.replace("_", " ")}</button>)}
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, number, notes…" data-testid="comm-search" className="h-9 min-w-[200px] flex-1 rounded-full border border-[#DDE2EA] bg-white px-4 text-sm outline-none focus:border-[#0E1F3F]" />
      </div>
      {items && !shown.length && <Empty text="Nothing logged yet. Tap 'Log contact' after each call, text or email." />}
      <div className="space-y-3">{shown.map((c) => <Row key={c.id} c={c} onEdit={setEditing} onDelete={remove} onStatus={setSt} />)}</div>
      <CommForm open={!!editing} entry={editing?.id ? editing : null} onOpenChange={(o) => !o && setEditing(null)} onSaved={save} />
    </div>
  );
}
